/**
 * Parts of How it works (HowItWorksSlot.jsx), Sept 2026 redesign: the package's role tabs
 * (site/how-it-works/index.html + site/pages.js `.role-tabs`, `.journey-panel`,
 * `.journey-grid`, `.step-label`; colours from site/flair.css) on the components/site
 * vocabulary and the brand tokens. Copy comes from the page through props.
 *
 * Deep links: each tab carries its role's fragment id (`#patrons`, `#businesses`,
 * `#venues`, the page's anchors since Phase 5c), so a link such as
 * /how-it-works#businesses selects that tab (read from the router location during render,
 * so the right panel is already showing when ScrollToTop looks for the target) and
 * ScrollToTop scrolls the tab strip into view below the fixed header; on in-app
 * navigation it also moves focus to the selected tab. Choosing a tab rewrites the URL
 * fragment in place (history.replaceState, the router's history state kept), like the
 * package, so the address can be shared, without a navigation (no scroll, no focus move).
 * A later change of the fragment always wins over an earlier tab choice: a router
 * navigation (new location key) or a native one (address bar, a plain `<a href="#…">`,
 * Back/Forward; the window's `hashchange`, which keeps the router key "default").
 * Aliases and other spellings (`#guests`, `#VENUES`) select their tab, give the tab strip
 * that exact id for the moment (so ScrollToTop and the browser find a target to scroll
 * to), and the address is rewritten to the canonical fragment (`#patrons`, `#venues`).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Eyebrow, Heading, SiteImage } from '@/components/site';

/** The role for a location hash ("#businesses" → "businesses"; aliases such as "#guests"), or null. */
export function roleForHash(hash, roles) {
  const key = String(hash || '').replace(/^#/, '').trim().toLowerCase();
  if (!key) return null;
  const hit = (roles || []).find((r) => r.id === key || (r.aliases || []).includes(key));
  return hit ? hit.id : null;
}

/** The element id a fragment points at, decoded as ScrollToTop and the browser do ("#VENUES" → "VENUES"). */
export function fragmentId(hash) {
  const raw = String(hash || '').replace(/^#/, '');
  try {
    return decodeURIComponent(raw);
  } catch (e) {
    return raw;
  }
}

/** Rewrite the URL fragment without a navigation (keeps React Router's history state). */
function replaceHash(pathname, search, id) {
  try {
    window.history.replaceState(window.history.state, '', `${pathname}${search}#${id}`);
  } catch (e) {
    // a sandboxed frame may refuse; the tab still switches
  }
}

// The package's journey cards (flair.css): sheet cards with a darker lilac top edge, every
// second one pale sage with a darker sage edge. The edges are mixed from the brand tokens
// (lilac or sage with 25% navy); the pale sage is the numbered cards' mix (audienceParts).
const CARD_TONES = [
  'bg-brand-sheet border-t-[color-mix(in_srgb,theme(colors.brand.lilac)_75%,theme(colors.navy.DEFAULT))]',
  'bg-[color-mix(in_srgb,theme(colors.brand.sage)_55%,theme(colors.brand.sheet))] border-t-[color-mix(in_srgb,theme(colors.brand.sage)_75%,theme(colors.navy.DEFAULT))]',
];

/**
 * One path's four steps (`.journey-grid`): an ordered list of cards, four columns from
 * 1051px, two from 481px, one below; each card has a navy "01 / For guests" pill, an h2
 * (the steps are the panel's headings, as in the package) and a muted paragraph.
 *
 * Each card opens with its step photo (upgrade brief §3.2: 4:3 from 800px, a 16:9 crop below;
 * decorative: the step's heading and text say what it shows).
 *
 * Props: `steps` [{ title, text, photo? }], `label` (the pill text and the list's name, e.g.
 * "For guests").
 */
export function JourneySteps({ steps = [], label }) {
  return (
    <ol
      role="list"
      aria-label={label ? `${label}, step by step` : undefined}
      className="grid gap-x-[22px] gap-y-[30px] min-[481px]:grid-cols-2 nav:gap-7 min-[1051px]:grid-cols-4"
    >
      {steps.map(({ title, text, photo }, i) => (
        <li
          key={title}
          className={cn(
            'site-light min-w-0 border border-t-4 border-brand-rule p-[22px] text-navy nav:p-6',
            CARD_TONES[i % CARD_TONES.length],
          )}
        >
          {photo ? (
            <SiteImage
              id={photo}
              ratio="4x3"
              box="16x9"
              navBox="4x3"
              decorative
              sizes="(min-width: 1808px) 406px, (min-width: 1051px) 24vw, (min-width: 481px) 48vw, 100vw"
              className="-mx-[22px] -mt-[22px] mb-5 w-[calc(100%+44px)] nav:-mx-6 nav:-mt-6 nav:w-[calc(100%+48px)]"
            />
          ) : null}
          <Eyebrow
            index={i + 1}
            className="rounded-full bg-navy px-[9px] py-1.5 text-[0.625rem] leading-[1.3] tracking-normal text-brand-sheet"
          >
            {label}
          </Eyebrow>
          {/* 31px at every width (the package's inline size); the line height is restated
              because a later font size drops Heading's own (tailwind-merge). */}
          <Heading
            level={2}
            size="section"
            className="mb-6 mt-5 text-[1.9375rem] leading-[1.06] nav:text-[1.9375rem]"
          >
            {title}
          </Heading>
          <p className="mb-4 text-base leading-[1.55] text-brand-muted">{text}</p>
        </li>
      ))}
    </ol>
  );
}

/**
 * The role tabs (WAI-ARIA tabs pattern, automatic activation): a pill-shaped tablist
 * centred above the panels (full width below 800px), one panel shown at a time.
 * - Keyboard: Left/Right (and Up/Down) move to the previous/next tab and select it (wrapping), Home/End
 *   the first/last; only the selected tab is in the Tab order (roving tabindex); Tab then
 *   moves into the panel (tabindex 0, its own focus ring), then to the panel's actions.
 * - Each tab's id is its role id, the page's deep-link anchor; `aliases` are accepted in
 *   the URL too ("#guests" for "#patrons", the package's key), in any case: the tab strip
 *   then carries that exact id (the scroll target; focus sent there goes on to the
 *   selected tab) and the address is rewritten to the canonical fragment.
 * - The fragment wins over an earlier tab choice whenever it changes: a router navigation
 *   (new location key) or a native change (`hashchange`: address bar, plain links,
 *   Back/Forward).
 * - Below 800px the three tabs share one row (as in the package). With enlarged text on a
 *   phone (under 20em: under 320px at 16px text, 640px at 32px) they stack, one per row;
 *   a label too long even then breaks inside its tab (hyphenated, overflow-wrap: anywhere
 *   as the last resort) instead of pushing the strip wider.
 * - Focus ring: drawn inside the tab (the strip clips its overflow for the rounded ends);
 *   rust on the sheet tabs (6.23:1), peach on the selected navy tab (`.site-dark`, 9.89:1).
 *
 * Props:
 * - `label`: the tablist's accessible name ("Choose your path")
 * - `roles`: [{ id, label, aliases?, content }] — `content` is the panel's node
 * - `className`: on the wrapper
 */
export function RoleTabs({ label, roles = [], className }) {
  const location = useLocation();
  const [picked, setPicked] = useState(null); // { id, key }: a tab chosen on this location
  const tabs = useRef({});

  const fromHash = roleForHash(location.hash, roles);
  // "#guests" or "#VENUES": no tab has that id, so the tab strip carries it (see below).
  const alias = fromHash && fragmentId(location.hash) !== fromHash ? fragmentId(location.hash) : null;

  // A native fragment change (address bar, a plain <a href="#…">, Back/Forward) keeps the
  // router's location key ("default" on a first load and after every native change), so
  // the key alone cannot tell it from the tab choice: `hashchange` (never fired by
  // replaceState or by the router's pushState) clears the choice and the fragment wins.
  // An alias typed there is rewritten to the canonical fragment too.
  useEffect(() => {
    const onHashChange = () => {
      setPicked(null);
      const { hash, pathname, search } = window.location;
      const id = roleForHash(hash, roles);
      if (id && fragmentId(hash) !== id) replaceHash(pathname, search, id);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [roles]);

  // A router location with an alias fragment (first load, a link): the address shows the
  // canonical one ("#patrons"), so a copied link names the tab's own anchor. The router
  // keeps its location, so ScrollToTop still looks for the alias: the tab strip.
  useEffect(() => {
    if (alias) replaceHash(location.pathname, location.search, fromHash);
  }, [alias, fromHash, location.key, location.pathname, location.search]);

  if (!roles.length) return null;

  const selected = (picked && picked.key === location.key && roles.some((r) => r.id === picked.id) ? picked.id : null)
    || fromHash
    || roles[0].id;

  const select = (id, focus = false) => {
    setPicked({ id, key: location.key });
    replaceHash(location.pathname, location.search, id);
    if (focus && tabs.current[id]) tabs.current[id].focus();
  };

  const onKeyDown = (e, index) => {
    let next = null;
    // Down/Up as well: with enlarged text on a phone the tabs stack in a column.
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (index + 1) % roles.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (index - 1 + roles.length) % roles.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = roles.length - 1;
    if (next === null) return;
    e.preventDefault();
    select(roles[next].id, true);
  };

  const panelId = (id) => `path-${id}`;

  // The alias target takes focus when ScrollToTop moves it there (in-app navigation; it
  // adds tabindex="-1"): hand it on to the selected tab, where a canonical link lands.
  const onStripFocus = (e) => {
    if (e.target === e.currentTarget && tabs.current[selected]) tabs.current[selected].focus({ preventScroll: true });
  };

  return (
    <div className={className}>
      <div
        role="tablist"
        aria-label={label}
        id={alias || undefined}
        onFocus={onStripFocus}
        className={cn(
          // corners as in the package (flair.css `.page-main .role-tabs`): 8px below 800px, a pill from 800px
          'mx-auto mb-[35px] mt-[3.4375rem] flex w-full scroll-mt-28 flex-nowrap overflow-hidden rounded-[8px] border border-navy shadow-[4px_4px_0] shadow-navy/[0.075]',
          // Phones (upgrade brief §3.2): the strip stays under the fixed header while the steps
          // scroll by, so the other paths are always one tap away; static from 800px.
          'max-[799px]:sticky max-[799px]:top-[4.875rem] max-[799px]:z-20',
          'nav:mb-[45px] nav:mt-16 nav:w-fit nav:scroll-mt-32 nav:flex-wrap nav:rounded-full',
          // enlarged text on a phone (under 20em): one tab per row, so a label keeps whole words
          '[@media(max-width:19.9375em)]:flex-col',
        )}
      >
        {roles.map((role, i) => {
          const on = role.id === selected;
          return (
            <button
              key={role.id}
              ref={(el) => { tabs.current[role.id] = el; }}
              type="button"
              role="tab"
              id={role.id}
              aria-selected={on}
              aria-controls={panelId(role.id)}
              tabIndex={on ? 0 : -1}
              onClick={() => select(role.id)}
              onKeyDown={(e) => onKeyDown(e, i)}
              className={cn(
                'min-h-12 min-w-0 flex-1 scroll-mt-28 border-r border-navy px-2 py-3.5 font-label text-[0.6875rem] leading-snug transition-colors duration-180 [overflow-wrap:anywhere] last:border-r-0',
                'focus-visible:outline-offset-[-6px] min-[481px]:px-[11px] min-[481px]:text-xs nav:flex-none nav:scroll-mt-32 nav:px-6 nav:py-[17px] nav:text-sm',
                // enlarged text on a phone: stacked (rules between rows), hyphenate before
                // breaking anywhere
                '[@media(max-width:19.9375em)]:border-b [@media(max-width:19.9375em)]:border-r-0 [@media(max-width:19.9375em)]:last:border-b-0 [@media(max-width:19.9375em)]:hyphens-auto',
                on ? 'site-dark bg-navy text-brand-sheet' : 'bg-brand-sheet text-navy hover:bg-brand-paper',
              )}
            >
              {role.label}
            </button>
          );
        })}
      </div>
      {roles.map((role) => (
        <div
          key={role.id}
          role="tabpanel"
          id={panelId(role.id)}
          aria-labelledby={role.id}
          hidden={role.id !== selected}
          tabIndex={0}
          className="pb-20 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-4 focus-visible:outline-brand-rust"
        >
          {role.content}
        </div>
      ))}
    </div>
  );
}
